import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon, Toaster } from 'anf-core-react';
import { useLazyQuery } from '@apollo/client';
import Tmnt from '../Tmnt/Tmnt';
import FreeShipping from '../Common/FreeShipping/FreeShipping';
import BagCta from '../Common/BagCta/BagCta';
import GET_ADD_TO_BAG_NOTIFICATION_DATA from '../../gql/addToBagNotification.gql';
import $window from '../../tools/window';

const TOASTER_ID = 'atb-notification';
export const SHOW_ATB_NOTIFICATION_EVENT = 'mfe:addToBag:displayMobileNotification';

export function AddToBagNotificationContent({
  textFor,
  freeShippingProgressInfo,
  bagCtaProps,
}) {
  return (
    <div data-testid="atb-notification" className="atb-notification-container scope-1892">
      <Tmnt tmnt={textFor.glbItemAddedToBag} attributes={{ className: 'h2' }} />
      <div className="atb-notification-warning-wrapper">
        <Icon
          icon="clock"
          labelText="atb-notification-warning"
          size="s"
        />
        <Tmnt tmnt={textFor.glbAtbNotificationsWarning} />
      </div>
      <FreeShipping {...freeShippingProgressInfo} tmntData={textFor} />
      <BagCta
        showCheckoutBtn={false}
        viewBagProps={bagCtaProps.viewBagProps}
        tmntData={textFor}
      />
    </div>
  );
}

export function AddToBagNotification({
  textFor,
  hideNotificationTimeout,
  bagCtaProps,
}) {
  const [isOpen, setIsOpen] = useState(false);

  const [getAddToBagNotificationData, { data }] = useLazyQuery(GET_ADD_TO_BAG_NOTIFICATION_DATA, {
    fetchPolicy: 'no-cache',
    context: { batch: true },
    ssr: false,
  });
  const { freeShippingProgressInfo = {} } = data || {};

  useEffect(() => {
    const handleDisplayMobileNotification = () => {
      getAddToBagNotificationData().then(() => {
        setIsOpen(true);
      });
    };

    $window.addEventListener(SHOW_ATB_NOTIFICATION_EVENT, handleDisplayMobileNotification);
    return () => {
      $window.removeEventListener(
        SHOW_ATB_NOTIFICATION_EVENT,
        handleDisplayMobileNotification,
      );
    };
  }, [getAddToBagNotificationData]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        setIsOpen(false);
      }, hideNotificationTimeout);
    }
  }, [hideNotificationTimeout, isOpen]);

  return (
    <div data-testid="atb-notification-wrapper">
      <Toaster direction="from-top" id={TOASTER_ID} isOpen={isOpen} isSticky>
        <AddToBagNotificationContent
          textFor={textFor}
          freeShippingProgressInfo={freeShippingProgressInfo}
          bagCtaProps={bagCtaProps}
        />
      </Toaster>
    </div>
  );
}

const textForPropTypes = PropTypes.shape({
  glbItemAddedToBag: PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  glbAtbNotificationsWarning: PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  freeShippingEarnedTMNTValue: PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  freeShippingAmountTMNTValue: PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  viewBag: PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  checkoutBtnTMNTValue: PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  takingYouToCheckout: PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
});

const freeShippingProgressInfoPropTypes = PropTypes.shape({
  isVisible: PropTypes.bool,
  amountToFreeShipping: PropTypes.number,
  progressValue: PropTypes.number,
  maxProgress: PropTypes.number,
  minProgress: PropTypes.number,
  postLabel: PropTypes.string,
  preLabel: PropTypes.string,
  amountToFreeShippingFmt: PropTypes.string,
});

const bagCtaPropTypes = PropTypes.shape({
  viewBagProps: PropTypes.shape({
    bagUrl: PropTypes.string.isRequired,
    variant: PropTypes.string.isRequired,
  }),
});

AddToBagNotificationContent.propTypes = {
  textFor: textForPropTypes.isRequired,
  freeShippingProgressInfo: freeShippingProgressInfoPropTypes.isRequired,
  bagCtaProps: bagCtaPropTypes.isRequired,
};

AddToBagNotification.propTypes = {
  hideNotificationTimeout: PropTypes.number.isRequired,
  textFor: textForPropTypes.isRequired,
  bagCtaProps: bagCtaPropTypes.isRequired,
};
